"use client";

import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Text,
} from "@chakra-ui/react";
import {
  phoneNumberService,
  type ImportPhoneNumberParams,
} from "../../voice-services/phoneNumberService";
import { useAuth } from "../../contexts/AuthContext";

interface ImportPhoneNumberDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ImportPhoneNumberDialog({
  isOpen,
  onClose,
  onSuccess,
}: ImportPhoneNumberDialogProps) {
  const { isAdmin, currentClientId, clients } = useAuth();

  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formData, setFormData] = React.useState<ImportPhoneNumberParams>({
    phone_number: "",
    termination_uri: "",
    sip_trunk_auth_username: "",
    sip_trunk_auth_password: "",
    nickname: "",
    inbound_webhook_url: null,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.phone_number || !formData.termination_uri) {
      toast({
        title: "Required fields missing",
        description: "Please fill in all required fields",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const currentClient = clients.find((c) => c.id === currentClientId);
      const dataToSubmit = {
        ...formData,
        ...(isAdmin && currentClient
          ? { company_name: currentClient.company_name }
          : {}),
      };

      await phoneNumberService.importPhoneNumber(dataToSubmit);
      toast({
        title: "Phone number imported",
        description: "Your phone number has been successfully imported.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error importing phone number:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : (error as any)?.response?.data?.message ||
            "An unexpected error occurred";
      toast({
        title: "Error importing phone number",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bg={"white"}>
        <ModalHeader color={"gray.800"}>
          Connect to your number via SIP trunking
        </ModalHeader>
        <ModalCloseButton color={"gray.800"} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel color={"gray.800"}>Phone Number</FormLabel>
              <Input
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="Enter phone number"
                bg={"white"}
                color={"gray.800"}
                borderColor={"gray.200"}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={"gray.800"}>Termination URI</FormLabel>
              <Input
                name="termination_uri"
                value={formData.termination_uri}
                onChange={handleChange}
                placeholder="Enter termination URI"
                bg={"white"}
                color={"gray.800"}
                borderColor={"gray.200"}
              />
            </FormControl>

            <FormControl>
              <FormLabel color={"gray.800"}>
                SIP Trunk User Name{" "}
                <Text as="span" color="gray.500">
                  (Optional)
                </Text>
              </FormLabel>
              <Input
                id="sip_trunk_auth_username"
                name="sip_trunk_auth_username"
                value={formData.sip_trunk_auth_username}
                onChange={handleChange}
                placeholder="Enter SIP Trunk User Name"
                bg={"white"}
                color={"gray.800"}
                borderColor={"gray.200"}
              />
            </FormControl>

            <FormControl>
              <FormLabel color={"gray.800"}>
                SIP Trunk Password{" "}
                <Text as="span" color="gray.500">
                  (Optional)
                </Text>
              </FormLabel>
              <Input
                id="sip_trunk_auth_password"
                name="sip_trunk_auth_password"
                value={formData.sip_trunk_auth_password}
                onChange={handleChange}
                placeholder="Enter SIP Trunk Password"
                type="password"
                bg={"white"}
                color={"gray.800"}
                borderColor={"gray.200"}
              />
            </FormControl>

            <FormControl>
              <FormLabel color={"gray.800"}>
                Nickname{" "}
                <Text as="span" color="gray.500">
                  (Optional)
                </Text>
              </FormLabel>
              <Input
                name="nickname"
                value={formData.nickname}
                onChange={handleChange}
                placeholder="Enter Nickname"
                bg={"white"}
                color={"gray.800"}
                borderColor={"gray.200"}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme={"white"}
            textColor={"gray.800"}
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            bg="primary.800"
            _hover={{ bg: "primary.900" }}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
